/**
 * @file 工作台首页
 * 原则：D1 只有工程基座，没有任何业务数据。这里一律渲染「空行」，
 * 绝不伪造数字（PRD 13 章验收要求「空状态清晰」「不假装有数据」）。
 *
 * 版式：页头写清「今天是谁的一天」，左侧按业务顺序排面板，右侧只放系统事实。
 * 取消问候语与装饰图标——每天要看 200 次的页面不需要寒暄。
 */

import { useAuthState } from '../auth/context';
import { SystemStatusCard } from './SystemStatusCard';

/** 一张首页面板。 */
export interface DashCard {
  /** 面板标题，如「今日课程」。 */
  title: string;
  /** 面板说明，讲清这块将来放什么。 */
  description: string;
  /** 空行标题。 */
  emptyTitle: string;
  /** 空行补充说明。 */
  emptyText: string;
  /** 右上角说明，如「排课接入后显示」。 */
  tag?: string;
}

/** 首页属性。 */
export interface DashboardProps {
  /** 页面标题。 */
  title: string;
  /** 页面副标题。 */
  subtitle: string;
  /** 面板配置（由各端传入）。 */
  cards: readonly DashCard[];
  /**
   * 是否展示「系统状态」面板。
   * 只给管理后台：探活、依赖、版本、运行时长属于运维信息，
   * 教师与销售看到只会干扰，也容易被误当成业务数据。
   */
  showSystemStatus?: boolean;
}

/** 今日提醒面板：业务端与管理端共用，空状态文案写清「提醒从哪来」。 */
const REMINDER_PANEL: DashCard = {
  title: '今日提醒',
  description: '结课待评语、作业待复核、试听待确认这几类，只由真实业务动作触发。',
  emptyTitle: '暂无提醒',
  emptyText: '通知中心排期在 D13，在那之前不填任何示例条目，空着就是确实没有。',
};

/**
 * @what 渲染一张面板（管理端与业务端共用同一套结构）
 * @param card 面板配置
 * @return JSX
 */
function renderPanel(card: DashCard) {
  return (
    <section className="lx-card" key={card.title}>
      <div className="lx-card__head">
        <h2 className="lx-card__title">{card.title}</h2>
        {card.tag ? <span className="lx-card__meta">{card.tag}</span> : null}
      </div>
      <div className="lx-card__body">
        <p className="lx-card__hint" style={{ margin: '0 0 10px' }}>
          {card.description}
        </p>
        <BlankNote title={card.emptyTitle} text={card.emptyText} />
      </div>
    </section>
  );
}

/**
 * @what 把日期格式化成中文短格式
 * @param date 当前时间
 * @return string 如「2026 年 11 月 5 日 周四」
 */
function formatToday(date: Date): string {
  const weekday = '日一二三四五六'[date.getDay()];
  return `${date.getFullYear()} 年 ${date.getMonth() + 1} 月 ${date.getDate()} 日 周${weekday}`;
}

/**
 * @what 工作台：页头 + 业务面板 + 系统状态
 * @param props.title 页面标题
 * @param props.subtitle 页面副标题
 * @param props.cards 面板配置
 * @return JSX
 */
export function Dashboard({ title, subtitle, cards, showSystemStatus = false }: DashboardProps) {
  const state = useAuthState();
  const roleNames = state.identity?.roles?.map((role) => role.name).join(' / ') ?? '未分配角色';
  const phone = state.identity?.masked_phone;
  const name = state.identity?.name;

  return (
    <>
      <header className="lx-page-head">
        <div className="lx-page-head__main">
          <h1 className="lx-page-head__title">{title}</h1>
          <p className="lx-page-head__sub">
            {formatToday(new Date())} · {name ? `${name} · ` : ''}
            {roleNames}
            {phone ? ` · ${phone}` : ''} · {subtitle}
          </p>
        </div>
        <div className="lx-page-head__aside">
          <span className="lx-badge lx-badge--outline">内测版本</span>
        </div>
      </header>

      {/* 管理后台：主列排业务面板，侧列放运维状态与提醒（两栏宽度不等，主次分明）。
          业务端：没有运维信息可放，四块面板直接两列等宽铺开，避免右侧留一大片空白。 */}
      {showSystemStatus ? (
        <div className="lx-grid lx-grid--dash">
          <div className="lx-stack">{cards.map(renderPanel)}</div>
          <div className="lx-stack">
            <SystemStatusCard />
            {renderPanel(REMINDER_PANEL)}
          </div>
        </div>
      ) : (
        <div className="lx-grid lx-grid--2">
          {cards.map(renderPanel)}
          {renderPanel(REMINDER_PANEL)}
        </div>
      )}
    </>
  );
}

/**
 * @what 空行：作业本上的一条空行，代替虚线空状态框
 * @param props.title 空行标题
 * @param props.text 说明
 * @return JSX
 */
export function BlankNote({ title, text }: { title: string; text: string }) {
  return (
    <div className="lx-blank">
      <div>
        <p className="lx-blank__title">{title}</p>
        <p className="lx-blank__text">{text}</p>
      </div>
    </div>
  );
}
